import api from "./api";

// const API_BASE_URL = "https://api.nephrotrax.com";

const clinicalTrialService = {
  // ==================== SIMPLIFIED APIs (for easy use) ====================

  /**
   * Get all trials with optional filters and pagination
   * Compatible with TrialBrowsePage component
   */
  getAllTrials: async (params = {}) => {
    try {
      console.log("📋 [getAllTrials] Fetching trials with params:", params);

      // Build query parameters
      const queryParams = {};

      // Pagination
      if (params.page !== undefined) queryParams.page = params.page;
      if (params.size !== undefined) queryParams.size = params.size;

      // Filters
      if (params.keyword) queryParams.keyword = params.keyword;
      if (params.ckdStage) queryParams.ckdStage = params.ckdStage;
      if (params.status) queryParams.status = params.status;
      if (params.phase) queryParams.phase = params.phase;
      if (params.targetOrgan) queryParams.targetOrgan = params.targetOrgan;

      const response = await api.get("/v1/clinical-trials", {
        params: queryParams,
      });

      console.log("✅ [getAllTrials] Success:", response.data);

      // Handle both paginated and non-paginated responses
      if (response.data.content) {
        return {
          success: true,
          data: {
            content: response.data.content,
            trials: response.data.content,
            totalElements: response.data.totalElements,
            totalCount: response.data.totalElements,
            totalPages: response.data.totalPages,
            currentPage: response.data.number,
            pageSize: response.data.size,
          },
        };
      } else {
        return {
          success: true,
          data: {
            content: response.data,
            trials: response.data,
            totalElements: response.data.length,
            totalCount: response.data.length,
            totalPages: 1,
            currentPage: 0,
            pageSize: response.data.length,
          },
        };
      }
    } catch (error) {
      console.error("❌ [getAllTrials] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch trials",
        data: {
          content: [],
          trials: [],
          totalElements: 0,
          totalCount: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: 20,
        },
      };
    }
  },

  /**
   * Get trial by ID - Simple method
   */
  getTrialById: async (id) => {
    try {
      console.log("🔍 [getTrialById] Fetching trial ID:", id);
      const response = await api.get(`/v1/clinical-trials/${id}`);
      console.log("✅ [getTrialById] Success");
      return { success: true, data: response.data };
    } catch (error) {
      console.error("❌ [getTrialById] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch trial",
      };
    }
  },

  /**
   * Get trial by NCT ID
   */
  getTrialByNctId: async (nctId) => {
    try {
      console.log("🔍 [getTrialByNctId] Fetching trial NCT ID:", nctId);
      const response = await api.get(`/v1/clinical-trials/nct/${nctId}`);
      console.log("✅ [getTrialByNctId] Success");
      return { success: true, data: response.data };
    } catch (error) {
      console.error("❌ [getTrialByNctId] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch trial",
      };
    }
  },

  /**
   * Search trials - Simple method
   */
  searchTrials: async (searchTerm, params = {}) => {
    try {
      console.log("🔍 [searchTrials] Searching for:", searchTerm);
      const response = await api.get("/v1/clinical-trials/search", {
        params: { keyword: searchTerm, ...params },
      });
      console.log("✅ [searchTrials] Found:", response.data.length, "trials");
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error("❌ [searchTrials] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to search trials",
        data: [],
      };
    }
  },

  /**
   * Filter trials with specific criteria
   */
  filterTrials: async (filters) => {
    try {
      console.log("🔍 [filterTrials] Filtering with:", filters);
      const response = await api.get("/v1/clinical-trials/filter", {
        params: filters,
      });
      console.log("✅ [filterTrials] Found:", response.data.length, "trials");
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error("❌ [filterTrials] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to filter trials",
        data: [],
      };
    }
  },

  /**
   * Get active/recruiting trials
   */
  getActiveTrials: async () => {
    try {
      console.log("🔍 [getActiveTrials] Fetching active trials...");
      const response = await api.get("/v1/clinical-trials/active");
      console.log("✅ [getActiveTrials] Found:", response.data.length, "trials");
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error("❌ [getActiveTrials] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch active trials",
        data: [],
      };
    }
  },

  /**
   * Get featured trials
   */
  getFeaturedTrials: async () => {
    try {
      console.log("⭐ [getFeaturedTrials] Fetching featured trials...");
      const response = await api.get("/v1/clinical-trials/featured");
      console.log("✅ [getFeaturedTrials] Found:", response.data.length, "trials");
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error("❌ [getFeaturedTrials] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch featured trials",
        data: [],
      };
    }
  },

  // ==================== INTERNAL DATABASE APIs ====================

  getAllInternalTrials: async () => {
    try {
      console.log("=== getAllInternalTrials ===");
      const response = await api.get("/v1/clinical-trials");
      return {
        success: true,
        data: {
          trials: response.data || [],
          totalCount: response.data?.length || 0,
          source: "internal",
        },
      };
    } catch (error) {
      console.error("Error fetching all internal trials:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch internal trials",
        data: { trials: [], totalCount: 0 },
      };
    }
  },

  searchInternalTrials: async (keyword) => {
    try {
      if (!keyword || keyword.trim() === "") {
        return clinicalTrialService.getAllInternalTrials();
      }
      const response = await api.get("/v1/clinical-trials/search", {
        params: { keyword },
      });
      return {
        success: true,
        data: {
          trials: response.data || [],
          totalCount: response.data?.length || 0,
          source: "internal",
        },
      };
    } catch (error) {
      console.error("Error searching internal trials:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to search internal trials",
        data: { trials: [], totalCount: 0 },
      };
    }
  },

  filterInternalTrials: async (filters) => {
    try {
      const response = await api.get("/v1/clinical-trials/filter", {
        params: filters,
      });
      return {
        success: true,
        data: {
          trials: response.data || [],
          totalCount: response.data?.length || 0,
          source: "internal",
        },
      };
    } catch (error) {
      console.error("Error filtering internal trials:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to filter trials",
        data: { trials: [], totalCount: 0 },
      };
    }
  },

  // ==================== EXTERNAL APIs (ClinicalTrials.gov) ====================

  searchExternalTrials: async (params) => {
    try {
      console.log("🌐 [searchExternalTrials] Searching external trials:", params);
      const response = await api.get("/v1/external-trials/search", {
        params: {
          query: params.condition || params.query,
          status: params.status,
          pageSize: params.pageSize || 20,
          pageToken: params.pageToken,
        },
      });
      console.log("✅ [searchExternalTrials] Found:", response.data.totalCount, "trials");
      return {
        success: true,
        data: {
          trials: response.data.trials || [],
          totalCount: response.data.totalCount || 0,
          returnedCount: response.data.returnedCount || 0,
          nextPageToken: response.data.nextPageToken,
          source: "external",
        },
      };
    } catch (error) {
      console.error("❌ [searchExternalTrials] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to search ClinicalTrials.gov",
        data: { trials: [], totalCount: 0 },
      };
    }
  },

  getExternalTrialByNctId: async (nctId) => {
    try {
      console.log("🌐 [getExternalTrialByNctId] Fetching external trial:", nctId);
      const response = await api.get(`/v1/external-trials/${nctId}`);
      console.log("✅ [getExternalTrialByNctId] Success");
      return { success: true, data: response.data };
    } catch (error) {
      console.error("❌ [getExternalTrialByNctId] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch external trial",
      };
    }
  },

  searchAmyloidosisTrials: async (params = {}) => {
    try {
      console.log("🌐 [searchAmyloidosisTrials] Searching amyloidosis trials");
      const response = await api.get("/v1/external-trials/amyloidosis", {
        params: {
          pageSize: params.pageSize || 20,
          pageToken: params.pageToken,
        },
      });
      return {
        success: true,
        data: {
          trials: response.data.trials || [],
          totalCount: response.data.totalCount || 0,
          returnedCount: response.data.returnedCount || 0,
          nextPageToken: response.data.nextPageToken,
          source: "external",
        },
      };
    } catch (error) {
      console.error("❌ [searchAmyloidosisTrials] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to search amyloidosis trials",
        data: { trials: [], totalCount: 0 },
      };
    }
  },

  // ==================== CRUD Operations ====================

  createTrial: async (trialData) => {
    try {
      console.log("➕ [createTrial] Creating trial...");
      const response = await api.post("/v1/clinical-trials", trialData);
      console.log("✅ [createTrial] Success!");
      return { success: true, data: response.data, message: "Trial created successfully" };
    } catch (error) {
      console.error("❌ [createTrial] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to create trial",
      };
    }
  },

  updateTrial: async (id, trialData) => {
    try {
      console.log("✏️ [updateTrial] Updating trial ID:", id);
      const response = await api.put(`/v1/clinical-trials/${id}`, trialData);
      console.log("✅ [updateTrial] Success!");
      return { success: true, data: response.data, message: "Trial updated successfully" };
    } catch (error) {
      console.error("❌ [updateTrial] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to update trial",
      };
    }
  },

  deleteTrial: async (id) => {
    try {
      console.log("🗑️ [deleteTrial] Deleting trial ID:", id);
      await api.delete(`/v1/clinical-trials/${id}`);
      console.log("✅ [deleteTrial] Success!");
      return { success: true, message: "Trial deleted successfully" };
    } catch (error) {
      console.error("❌ [deleteTrial] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to delete trial",
      };
    }
  },

  // ==================== PATIENT MATCHING ====================

  matchPatientToTrials: async (matchData) => {
    try {
      console.log("🎯 [matchPatientToTrials] Matching patient to trials...");
      const response = await api.post("/v1/patient-matching/match", matchData);
      console.log("✅ [matchPatientToTrials] Success:", response.data.totalMatches, "matches");
      return response.data;
    } catch (error) {
      console.error("❌ [matchPatientToTrials] Error:", error);
      throw error;
    }
  },

  savePatientMatch: async (matchData) => {
    try {
      console.log("💾 [savePatientMatch] Saving match results...");
      const response = await api.post("/v1/patient-matching/save", matchData);
      console.log("✅ [savePatientMatch] Success");
      return { success: true, data: response.data, message: "Match results saved successfully" };
    } catch (error) {
      console.error("❌ [savePatientMatch] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to save match results",
      };
    }
  },

  // ==================== STATISTICS & ANALYTICS ====================

  getTrialStatistics: async () => {
    try {
      console.log("📊 [getTrialStatistics] Fetching statistics...");
      const response = await api.get("/v1/clinical-trials/statistics");
      console.log("✅ [getTrialStatistics] Success");
      return { success: true, data: response.data };
    } catch (error) {
      console.error("❌ [getTrialStatistics] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch statistics",
        data: {},
      };
    }
  },

  getTrialsByCkdStage: async (ckdStage) => {
    try {
      console.log("🔍 [getTrialsByCkdStage] Fetching trials for stage:", ckdStage);
      const response = await api.get(`/v1/clinical-trials/ckd-stage/${ckdStage}`);
      console.log("✅ [getTrialsByCkdStage] Found:", response.data.length, "trials");
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error("❌ [getTrialsByCkdStage] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch trials",
        data: [],
      };
    }
  },

  getTrialsByStatus: async (status) => {
    try {
      console.log("🔍 [getTrialsByStatus] Fetching trials with status:", status);
      const response = await api.get(`/v1/clinical-trials/status/${status}`);
      console.log("✅ [getTrialsByStatus] Found:", response.data.length, "trials");
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error("❌ [getTrialsByStatus] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch trials",
        data: [],
      };
    }
  },

  getTrialsByPhase: async (phase) => {
    try {
      console.log("🔍 [getTrialsByPhase] Fetching trials for phase:", phase);
      const response = await api.get(`/v1/clinical-trials/phase/${phase}`);
      console.log("✅ [getTrialsByPhase] Found:", response.data.length, "trials");
      return { success: true, data: response.data || [] };
    } catch (error) {
      console.error("❌ [getTrialsByPhase] Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || "Failed to fetch trials",
        data: [],
      };
    }
  },
};

export default clinicalTrialService;